// eslint-disable-next-line max-classes-per-file
import { Extension, Reflection, Text, Type, Uri } from 'main.core';
import { type MenuItem } from 'main.popup';

import 'sidepanel';

let instance = null;

const CALL_ASSESSMENT_SLIDER_WIDTH = 1045;
const LEGACY_CALL_ASSESSMENT_SLIDER_WIDTH = 700;

class ListViewTypes
{
	static KANBAN = 'KANBAN';
	static LIST = 'LIST';
	static ACTIVITY = 'ACTIVITY';
}

declare type UrlTemplatesSettings = {
	defaultRootUrlTemplates: UrlTemplates,
	customRootUrlTemplates: CustomRootUrlTemplates,
};

declare type UrlTemplates = Object<string, string>;

declare type CustomRootUrlTemplates = Object<number, UrlTemplates>;

/**
 * @memberOf BX.Crm
 */
class Router
{
	defaultRootUrlTemplates: UrlTemplates = {};
	customRootUrlTemplates: CustomRootUrlTemplates = {};
	currentViews: Object<number, string> = {};

	static get Instance(): Router
	{
		if ((window.top !== window) && Reflection.getClass('top.BX.Crm.Router'))
		{
			return window.top.BX.Crm.Router.Instance;
		}

		if (instance === null)
		{
			instance = new Router();
		}

		return instance;
	}

	/**
	 * @public
	 * @param params
	 * @return {BX.Crm.Router}
	 */
	setUrlTemplates(params: UrlTemplatesSettings): Router
	{
		if (Type.isPlainObject(params.defaultRootUrlTemplates))
		{
			this.defaultRootUrlTemplates = params.defaultRootUrlTemplates;
		}

		if (Type.isPlainObject(params.customRootUrlTemplates))
		{
			this.customRootUrlTemplates = params.customRootUrlTemplates;
		}

		return this;
	}

	setCurrentListView(entityTypeId: number, view: string): Router
	{
		this.currentViews[entityTypeId] = view;

		return this;
	}

	getCurrentListView(entityTypeId: number): string
	{
		return this.currentViews[entityTypeId] || ListViewTypes.KANBAN;
	}

	static openSlider(url: string | Uri, options: ?Object = null): Promise<?BX.SidePanel.Slider>
	{
		const preparedUrl = String(url);
		if (!Type.isStringFilled(preparedUrl))
		{
			return Promise.resolve();
		}

		let preparedOptions = Type.isPlainObject(options) ? options : {};
		preparedOptions = { cacheable: false, allowChangeHistory: true, events: {}, ...preparedOptions };

		return new Promise((resolve) => {
			preparedOptions.events.onClose = (event) => resolve(event.getSlider());

			BX.SidePanel.Instance.open(preparedUrl, preparedOptions);
		});
	}

	openTypeDetail(typeId: number, options: ?{}, queryParams: ?{}): ?Promise<?BX.SidePanel.Slider>
	{
		const preparedOptions = Type.isPlainObject(options) ? options : {};

		preparedOptions.width = 876;
		preparedOptions.allowChangeHistory = false;
		preparedOptions.cacheable = false;

		const uri = this.getTypeDetailUrl(typeId);
		if (uri)
		{
			if (Type.isPlainObject(queryParams))
			{
				uri.setQueryParams(queryParams);
			}

			return Router.openSlider(uri.toString(), preparedOptions);
		}

		return null;
	}

	openAutomatedSolutionDetail(automatedSolutionId: number = 0, options: {} = {}): ?Promise<?BX.SidePanel.Slider>
	{
		const preparedOptions = Type.isPlainObject(options) ? options : {};

		preparedOptions.width = 876;
		preparedOptions.allowChangeHistory = false;
		preparedOptions.cacheable = false;

		const uri = this.getAutomatedSolutionDetailUrl(automatedSolutionId);
		if (uri)
		{
			return Router.openSlider(uri, preparedOptions);
		}

		return null;
	}

	/**
	 * @protected
	 * @param component
	 * @param entityTypeId
	 * @return {string|null}
	 */
	getTemplate(component: string, entityTypeId: number = 0): ?string
	{
		if ((entityTypeId > 0) && Object.hasOwn(this.customRootUrlTemplates, entityTypeId))
		{
			return this.customRootUrlTemplates[entityTypeId][component] ?? null;
		}

		return this.defaultRootUrlTemplates[component] ?? null;
	}

	getTypeDetailUrl(entityTypeId: number = 0): ?Uri
	{
		const template = this.getTemplate('bitrix:crm.type.detail', entityTypeId);
		if (template)
		{
			return new Uri(template.replace('#entityTypeId#', entityTypeId));
		}

		return null;
	}

	getTypeListUrl(): ?Uri
	{
		const template = this.getTemplate('bitrix:crm.type.list');
		if (template)
		{
			return new Uri(template);
		}

		return null;
	}

	openTypeHelpPage()
	{
		Router.openHelper(null, 13_315_798);
	}

	static openHelper(event: Event = null, code: number = null)
	{
		if (event && Type.isFunction(event.preventDefault))
		{
			event.preventDefault();
		}

		if (top.BX.Helper && code > 0)
		{
			top.BX.Helper.show(`redirect=detail&code=${code}`);
		}
	}

	showFeatureSlider(event, item, sliderCode: string = 'limit_smart_process_automation')
	{
		Router.Instance.closeSettingsMenu(event, item);

		if (Reflection.getClass('BX.UI.InfoHelper.show'))
		{
			BX.UI.InfoHelper.show(sliderCode);
		}
	}

	/**
	 * For dynamic entities only.
	 * Does not support knowledge about whether kanban available or not.
	 *
	 * @param entityTypeId
	 * @param categoryId
	 */
	getItemListUrlInCurrentView(entityTypeId: number, categoryId: ?number = 0): ?Uri
	{
		const currentListView = this.getCurrentListView(entityTypeId);
		let template = null;
		if (currentListView === ListViewTypes.KANBAN)
		{
			template = this.getTemplate('bitrix:crm.item.kanban', entityTypeId);
		}
		else if (currentListView === ListViewTypes.ACTIVITY)
		{
			template = this.getTemplate('bitrix:crm.item.activities', entityTypeId);
		}
		else
		{
			template = this.getTemplate('bitrix:crm.item.list', entityTypeId);
		}

		if (template)
		{
			return new Uri(template.replace('#entityTypeId#', entityTypeId).replace('#categoryId#', categoryId));
		}

		return null;
	}

	/**
	 * For factory based entities only.
	 * Does not support knowledge about whether kanban available or not.
	 *
	 * @public
	 * @param entityTypeId
	 * @param categoryId
	 * @return {null|BX.Uri}
	 */
	getKanbanUrl(entityTypeId: number, categoryId: ?number = 0): ?Uri
	{
		const template = this.getTemplate('bitrix:crm.item.kanban', entityTypeId);
		if (template)
		{
			return new Uri(template.replace('#entityTypeId#', entityTypeId).replace('#categoryId#', categoryId));
		}

		return null;
	}

	/**
	 * For factory based entities only
	 *
	 * @public
	 * @param entityTypeId
	 * @param categoryId
	 * @return {null|BX.Uri}
	 */
	getItemListUrl(entityTypeId: number, categoryId: ?number = 0): ?Uri
	{
		const template = this.getTemplate('bitrix:crm.item.list', entityTypeId);
		if (template)
		{
			return new Uri(template.replace('#entityTypeId#', entityTypeId).replace('#categoryId#', categoryId));
		}

		return null;
	}

	openDocumentSlider(documentId: number): Promise<?BX.SidePanel.Slider>
	{
		return Router.openSlider(
			`/bitrix/components/bitrix/crm.document.view/slider.php?documentId=${documentId}`,
			{
				width: 1060,
				loader: '/bitrix/components/bitrix/crm.document.view/templates/.default/images/document_view.svg',
			},
		);
	}

	openSignDocumentSlider(documentId: number, memberHash: string): Promise<?BX.SidePanel.Slider>
	{
		// todo make a url template
		return Router.openSlider(
			`/bitrix/components/bitrix/crm.signdocument.view/slider.php?documentId=${documentId}&memberHash=${memberHash}`,
			{
				width: 1060,
			},
		);
	}

	openSignDocumentModifySlider(documentId: number): Promise<?BX.SidePanel.Slider>
	{
		return Router.openSlider(
			`/sign/doc/0/?docId=${documentId}&stepId=changePartner&noRedirect=Y`,
			{
				width: 1250,
			},
		);
	}

	openCalendarEventSlider(eventId: number, isSharing: boolean): Promise<?BX.SidePanel.Slider>
	{
		const sliderId = `crm-calendar-slider-${eventId}-${Math.floor(Math.random() * 1000)}`;

		return new (window.top.BX || window.BX).Calendar.SliderLoader(eventId, {
			sliderId,
			isSharing,
		}).show();
	}

	openRepeatSaleSegmentSlider(
		segmentId: number,
		readOnly: boolean = true,
		analytics: Object = {},
	): Promise<?BX.SidePanel.Slider>
	{
		return Router.openSlider(
			`/crm/repeat-sale-segment/details/${segmentId}/`,
			{
				width: 922,
				cacheable: false,
				requestMethod: 'post',
				requestParams: {
					readOnly,
					analytics,
				},
			},
		);
	}

	openCallAssessmentSlider(assessmentSettingId: ?number = null, options: ?Object = {}): Promise<?BX.SidePanel.Slider>
	{
		const { legacyWidth, ...restOptions } = Type.isPlainObject(options) ? options : {};

		const route = Type.isInteger(assessmentSettingId)
			? `/crm/copilot-call-assessment/details/${assessmentSettingId}/`
			: '/crm/copilot-call-assessment/';

		const isV2Enabled = Extension.getSettings('crm.router').get('isCallScoringV2Enabled') === true;
		const width = isV2Enabled
			? CALL_ASSESSMENT_SLIDER_WIDTH
			: (Type.isNumber(legacyWidth) ? legacyWidth : LEGACY_CALL_ASSESSMENT_SLIDER_WIDTH);

		return Router.openSlider(route, {
			width,
			cacheable: false,
			...restOptions,
		});
	}

	closeSettingsMenu(event, item)
	{
		if (item && Type.isFunction(item.getMenuWindow))
		{
			const window = item.getMenuWindow();
			if (window)
			{
				window.close();

				return;
			}
		}
		// eslint-disable-next-line unicorn/no-this-assignment
		const menu = this;
		if (menu && Type.isFunction(menu.close))
		{
			menu.close();
		}
	}

	closeToolbarSettingsMenuRecursively(event: PointerEvent, menuItem: MenuItem): void
	{
		let menuWindow = menuItem?.getMenuWindow();
		if (!menuWindow)
		{
			return;
		}

		while (menuWindow)
		{
			menuWindow.close();
			menuWindow = menuWindow.getParentMenuWindow();
		}
	}

	closeSliderOrRedirect(redirectTo: string | Uri, currentWindow: ?Window = null): void
	{
		const slider: ?BX.SidePanel.Slider = BX.SidePanel?.Instance?.getSliderByWindow(currentWindow ?? window);
		if (slider)
		{
			slider.close();

			return;
		}

		if (redirectTo instanceof Uri)
		{
			window.location.href = redirectTo.toString();
		}
		else
		{
			window.location.href = redirectTo;
		}
	}

	getAiReportDrawerUrl(scenario: string, drawerRequest: Object = {}): ?Uri
	{
		const isAvailableScenario = (
			Type.isStringFilled(scenario)
			&& ['call-assessment', 'summary-history', 'transcript'].includes(scenario)
		);
		if (!isAvailableScenario)
		{
			return null;
		}

		const queryParams = this.#prepareAiReportDrawerQueryParams(drawerRequest);
		if (queryParams === null)
		{
			return null;
		}

		return new Uri(`/crm/ai-report-drawer/${scenario}/`).setQueryParams(queryParams);
	}

	openAiReportDrawer(
		scenario: string,
		drawerRequest: Object = {},
		options: ?Object = {},
	): ?Promise<?BX.SidePanel.Slider>
	{
		const uri = this.getAiReportDrawerUrl(scenario, drawerRequest);
		if (uri)
		{
			return this.#openAiReportDrawerSlider(uri, options);
		}

		return null;
	}

	openAiReportDrawerOrLegacy(
		scenario: string,
		drawerRequest: Object,
		openLegacy: () => mixed,
		options: ?Object = {},
	): ?Promise<?BX.SidePanel.Slider>
	{
		try
		{
			if (this.#prepareAiReportDrawerQueryParams(drawerRequest) === null)
			{
				return null;
			}

			const isEnabled = Extension.getSettings('crm.router').get('isCallScoringV2Enabled') === true;
			if (isEnabled)
			{
				const uri = this.getAiReportDrawerUrl(scenario, drawerRequest);
				if (uri)
				{
					return this.#openAiReportDrawerSlider(uri, options);
				}
			}
		}
		catch
		{
			// Fall back only while the common drawer is being prepared.
		}

		if (Type.isFunction(openLegacy))
		{
			openLegacy();
		}

		return null;
	}

	getAutomatedSolutionListUrl(): ?Uri
	{
		return new Uri('/automation/type/automated_solution/list/');
	}

	getAutomatedSolutionDetailUrl(id: number): ?Uri
	{
		let normalizedId = Text.toInteger(id);
		normalizedId = normalizedId > 0 ? normalizedId : 0;

		return new Uri(`/automation/type/automated_solution/details/${normalizedId}/`);
	}

	getDealKanbanUrl(categoryId: number): ?Uri
	{
		let normalizedId = Text.toInteger(categoryId);
		normalizedId = normalizedId > 0 ? normalizedId : 0;

		return new Uri(`/crm/deal/kanban/category/${normalizedId}/`);
	}

	getItemDetailUrl(entityTypeId: number, entityId: number, categoryId: ?number = null): ?Uri
	{
		const normalizedEntityTypeId = Text.toInteger(entityTypeId);
		const normalizedEntityId = Text.toInteger(entityId);
		const normalizedCategoryId = Type.isNil(categoryId) ? null : Text.toInteger(categoryId);

		if (!BX.CrmEntityType.isDefined(normalizedEntityTypeId) || normalizedEntityId < 0)
		{
			return null;
		}

		let template = this.#getItemDetailUrlTemplate(normalizedEntityTypeId);
		if (template === null && BX.CrmEntityType.isDynamicTypeByTypeId(normalizedEntityTypeId))
		{
			template = this.getTemplate('bitrix:crm.item.details');
		}

		if (!template)
		{
			return null;
		}

		template = template.replace('#entityId#', normalizedEntityId);

		template = template.replace('#ENTITY_ID#', normalizedEntityId);
		template = template.replace('#ENTITY_TYPE_ID#', normalizedEntityTypeId);

		const uri = new Uri(template);

		if (!Type.isNil(normalizedCategoryId))
		{
			uri.setQueryParam('category_id', normalizedCategoryId);
		}

		return uri;
	}

	#getItemDetailUrlTemplate(entityTypeId: number): ?string
	{
		return this.#getItemDetailUrlTemplateMap()[entityTypeId] ?? null;
	}

	#getItemDetailUrlTemplateMap(): Object
	{
		/**
		 * Because BX.Crm.Router is not initialized in every component, we can lose URL templates
		 * todo: get URL templates from the backend
		 */
		return {
			[BX.CrmEntityType.enumeration.lead]: '/crm/lead/details/#entityId#/',
			[BX.CrmEntityType.enumeration.deal]: '/crm/deal/details/#entityId#/',
			[BX.CrmEntityType.enumeration.contact]: '/crm/contact/details/#entityId#/',
			[BX.CrmEntityType.enumeration.company]: '/crm/company/details/#entityId#/',
			[BX.CrmEntityType.enumeration.quote]: `/crm/type/${BX.CrmEntityType.enumeration.quote}/details/#entityId#/`,
			[BX.CrmEntityType.enumeration.smartinvoice]: `/crm/type/${BX.CrmEntityType.enumeration.smartinvoice}/details/#entityId#/`,
		};
	}

	#prepareAiReportDrawerQueryParams(drawerRequest: Object): ?Object
	{
		if (!Type.isPlainObject(drawerRequest))
		{
			return null;
		}

		const activityId = Text.toInteger(drawerRequest.activityId);
		const ownerTypeId = Text.toInteger(drawerRequest.ownerTypeId);
		const ownerId = Text.toInteger(drawerRequest.ownerId);
		if (activityId <= 0 || ownerTypeId <= 0 || ownerId <= 0)
		{
			return null;
		}

		const queryParams = {
			activityId,
			ownerTypeId,
			ownerId,
		};

		const jobId = Text.toInteger(drawerRequest.jobId);
		if (jobId > 0)
		{
			queryParams.jobId = jobId;
		}

		const assessmentSettingsId = Text.toInteger(drawerRequest.assessmentSettingsId);
		if (assessmentSettingsId > 0)
		{
			queryParams.assessmentSettingsId = assessmentSettingsId;
		}

		return queryParams;
	}

	#openAiReportDrawerSlider(
		uri: Uri,
		options: ?Object = {},
	): Promise<?BX.SidePanel.Slider>
	{
		const preparedOptions = {
			width: 800,
			cacheable: false,
			allowChangeHistory: false,
			containerClassName: 'crm-ai-report-drawer-slider',
			copyLinkLabel: true,
			newWindowLabel: false,
			newWindowUrl: uri.toString(),
			...(Type.isPlainObject(options) ? options : {}),
		};

		return Router.openSlider(uri, preparedOptions);
	}

	openMessageSenderConnectionsSlider(analytics: Object = {}): Promise<?BX.SidePanel.Slider>
	{
		const url = new Uri('/crm/messagesender/connections/').setQueryParams({ analytics }).toString();

		return Router.openSlider(
			url,
			{
				width: 920,
				cacheable: false,
			},
		);
	}
}

export {
	Router,
};
